import { apiClient, getAuthHeaders } from './api';

export interface InvoiceResponse {
  success: boolean;
  message: string;
  invoice_id?: string;
  invoice_number?: number;
  pdf_path?: string;
  download_url?: string;
  error?: string;
}

export interface InvoiceGenerateRequest {
  force_regenerate?: boolean;
}

export interface InvoiceListResponse {
  success: boolean;
  invoices: InvoiceItem[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export type InvoiceSortBy = 'invoice_date' | 'due_date' | 'invoice_number' | 'amount';
export type InvoiceSortDir = 'asc' | 'desc';

export interface InvoiceListOptions {
  /** Matches invoice number or Epicor order number, partial allowed. */
  search?: string;
  year?: number | 'all';
  sortBy?: InvoiceSortBy;
  sortDir?: InvoiceSortDir;
}

export interface InvoiceItem {
  id: string;
  invoice_number: number;
  // Mirrors backend InvoiceStatus enum (backend/app/models/invoice.py).
  status: 'DRAFT' | 'POSTED' | 'PAID' | 'CLOSED' | 'CANCELLED';
  total_amount: number;
  tax_amount: number;
  currency: string;
  invoice_date: string | null;
  due_date: string | null;
  is_pdf_generated: boolean;
  order_id: string;
  order_number: number | null;
  created_at: string;
  updated_at: string;
  download_url: string | null;
  payment_url: string | null;
}

export interface DmsDocumentResponse {
  success: boolean;
  dms_enabled: boolean;
  status_documents: 'Complete' | 'Incomplete' | null;
  missing_documents?: string[];
  signed_invoice?: {
    url: string;
    sign_status: string;
    requires_ematerai: boolean;
  } | null;
  delivery_notes?: Array<{
    dn_code: string;
    filename: string;
    url: string;
  }>;
  faktur_pajak?: Array<{
    nomor_faktur: string;
    filename: string;
    url: string;
  }>;
  last_fetched_at?: string | null;
  fetch_error?: string | null;
}

export interface PaymentResponse {
  success: boolean;
  message: string;
  payment_transaction?: {
    id: string;
    invoice_id: string;
    order_id: string;
    midtrans_order_id: string;
    midtrans_transaction_id: string | null;
    snap_token: string | null;
    redirect_url: string | null;
    amount: number;
    currency: string;
    formatted_amount: string;
    payment_method: string | null;
    payment_type: string | null;
    status: string;
    midtrans_status: string | null;
    midtrans_fraud_status: string | null;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    is_sandbox: boolean;
    is_pending: boolean;
    is_successful: boolean;
    is_failed: boolean;
    is_expired: boolean;
    created_at: string;
    updated_at: string;
    paid_at: string | null;
    expired_at: string | null;
    snap_payment_url: string;
  };
  snap_token?: string;
  redirect_url?: string;
  invoice_id?: string;
  amount?: number;
  currency?: string;
  // Legacy field for backward compatibility
  payment_url?: string;
}

class InvoiceService {
  private baseUrl = '/invoices';

  async getOrderInvoice(orderId: string): Promise<InvoiceResponse> {
    return await apiClient.get(`${this.baseUrl}/order/${orderId}`, getAuthHeaders());
  }

  async generateOrderInvoice(orderId: string, request: InvoiceGenerateRequest = {}): Promise<InvoiceResponse> {
    return await apiClient.post(`${this.baseUrl}/order/${orderId}/generate`, request, getAuthHeaders());
  }

  async downloadInvoice(invoiceId: string): Promise<void> {
    const headers = getAuthHeaders();
    const url = `${process.env.REACT_APP_API_BASE_URL}${this.baseUrl}/${invoiceId}/download`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download invoice: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    
    // Open in new tab instead of downloading
    window.open(downloadUrl, '_blank');
    
    // Clean up the URL after a delay to ensure it loads
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  }

  getDownloadUrl(invoiceId: string): string {
    return `${process.env.REACT_APP_API_BASE_URL}${this.baseUrl}/${invoiceId}/download`;
  }

  async listInvoices(
    skip: number = 0,
    limit: number = 50,
    statusFilter?: string,
    options: InvoiceListOptions = {},
  ): Promise<InvoiceListResponse> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (statusFilter && statusFilter !== 'all') {
      params.append('status_filter', statusFilter);
    }

    const { search, year, sortBy, sortDir } = options;
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    if (year && year !== 'all') {
      params.append('year', year.toString());
    }
    if (sortBy) {
      params.append('sort_by', sortBy);
    }
    if (sortDir) {
      params.append('sort_dir', sortDir);
    }

    return await apiClient.get(`${this.baseUrl}/list?${params.toString()}`, getAuthHeaders());
  }

  async processPayment(invoiceId: string): Promise<PaymentResponse> {
    return await apiClient.post(`${this.baseUrl}/${invoiceId}/payment`, {}, getAuthHeaders());
  }

  async markPaid(invoiceId: string): Promise<any> {
    return await apiClient.post(`${this.baseUrl}/${invoiceId}/mark-paid`, {}, getAuthHeaders());
  }

  async downloadReceipt(invoiceId: string): Promise<void> {
    const headers = getAuthHeaders();
    const url = `${process.env.REACT_APP_API_BASE_URL}${this.baseUrl}/${invoiceId}/receipt`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download receipt: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    
    // Open in new tab instead of downloading
    window.open(downloadUrl, '_blank');
    
    // Clean up the URL after a delay to ensure it loads
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  }

  getReceiptDownloadUrl(invoiceId: string): string {
    return `${process.env.REACT_APP_API_BASE_URL}${this.baseUrl}/${invoiceId}/receipt`;
  }

  async getDmsDocuments(invoiceId: string, refresh: boolean = false): Promise<DmsDocumentResponse> {
    const params = refresh ? '?refresh=true' : '';
    return await apiClient.get(
      `${this.baseUrl}/${invoiceId}/dms-documents${params}`,
      getAuthHeaders(),
    );
  }

  async downloadDmsDocument(invoiceId: string, docType: string, docIndex: number = 0): Promise<void> {
    const headers = getAuthHeaders();
    const url = `${process.env.REACT_APP_API_BASE_URL}${this.baseUrl}/${invoiceId}/dms-documents/proxy/${docType}/${docIndex}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { ...headers },
    });

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    window.open(downloadUrl, '_blank');
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  }
}

export const invoiceService = new InvoiceService();
export default invoiceService;